import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Shield, CheckCircle2, AlertTriangle, FileText, RefreshCw, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SOC2ReadinessPage() {
  const [readiness, setReadiness] = useState(null);
  const [controls, setControls] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(false);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('grc_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [readinessRes, controlsRes, checklistRes] = await Promise.all([
        axios.get(`${API}/api/soc2/readiness`, { headers }),
        axios.get(`${API}/api/soc2/controls`, { headers }),
        axios.get(`${API}/api/soc2/checklist`, { headers })
      ]);

      setReadiness(readinessRes.data);
      setControls(controlsRes.data.controls || []);
      setChecklist(checklistRes.data);
    } catch (error) {
      toast.error('Failed to load SOC 2 data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAutoCollect = async () => {
    setCollecting(true);
    try {
      const token = localStorage.getItem('grc_token');
      const res = await axios.post(`${API}/api/soc2/auto-collect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Collected ${res.data.collected_count} evidence items`);
      fetchData();
    } catch (error) {
      toast.error('Auto-collection failed');
    } finally {
      setCollecting(false);
    }
  };

  const handleChecklistToggle = async (itemId, completed) => {
    try {
      const token = localStorage.getItem('grc_token');
      await axios.post(`${API}/api/soc2/checklist/${itemId}?completed=${!completed}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to update checklist');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'compliant':
        return <Badge className="bg-green-100 text-green-800">Compliant</Badge>;
      case 'partial':
        return <Badge className="bg-yellow-100 text-yellow-800">Partial</Badge>;
      default:
        return <Badge className="bg-red-100 text-red-800">Gap</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="soc2-readiness-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">SOC 2 Readiness</h1>
          <p className="text-muted-foreground">Track your SOC 2 Type II compliance progress</p>
        </div>
        <Button onClick={handleAutoCollect} disabled={collecting} data-testid="auto-collect-btn">
          <RefreshCw className={`mr-2 h-4 w-4 ${collecting ? 'animate-spin' : ''}`} />
          {collecting ? 'Collecting...' : 'Auto-Collect Evidence'}
        </Button>
      </div>

      {/* Score Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="overall-score-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Overall Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-4xl font-bold ${getScoreColor(readiness?.overall_score || 0)}`}>
              {(readiness?.overall_score || 0).toFixed(1)}%
            </div>
            <Progress value={readiness?.overall_score || 0} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Audit Ready</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {readiness?.ready_for_audit ? (
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              ) : (
                <AlertTriangle className="h-8 w-8 text-yellow-500" />
              )}
              <span className="text-lg font-semibold">
                {readiness?.ready_for_audit ? 'Yes' : 'Not Yet'}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Controls</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{controls.length}</div>
            <p className="text-xs text-muted-foreground">Trust Service Criteria</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Checklist Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {checklist?.overall_progress || 0}%
            </div>
            <Progress value={checklist?.overall_progress || 0} className="mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="controls" className="space-y-4">
        <TabsList>
          <TabsTrigger value="controls" data-testid="controls-tab">Controls</TabsTrigger>
          <TabsTrigger value="checklist" data-testid="checklist-tab">Audit Checklist</TabsTrigger>
          <TabsTrigger value="gaps" data-testid="gaps-tab">Gaps & Recommendations</TabsTrigger>
        </TabsList>

        <TabsContent value="controls">
          <Card>
            <CardHeader>
              <CardTitle>SOC 2 Controls</CardTitle>
              <CardDescription>19 controls mapped to Trust Service Criteria</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {controls.map((control) => (
                  <div
                    key={control.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                    data-testid={`control-${control.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <Shield className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium">{control.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {control.category} • {control.principle}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge variant="outline">{control.evidence_types?.length || 0} evidence types</Badge>
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="checklist">
          <Card>
            <CardHeader>
              <CardTitle>Audit Preparation Checklist</CardTitle>
              <CardDescription>
                {checklist?.total_items || 0} items across {checklist?.phases?.length || 4} phases
              </CardDescription>
            </CardHeader>
            <CardContent>
              {checklist?.phases?.map((phase) => (
                <div key={phase.phase} className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold capitalize">{phase.phase.replace('_', ' ')}</h3>
                    <Badge variant="secondary">{phase.progress}%</Badge>
                  </div>
                  <div className="space-y-2">
                    {phase.items?.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center gap-3 p-3 border rounded-lg"
                        data-testid={`checklist-item-${item.id}`}
                      >
                        <Checkbox
                          checked={item.completed}
                          onCheckedChange={() => handleChecklistToggle(item.id, item.completed)}
                        />
                        <div className="flex-1">
                          <p className={item.completed ? 'line-through text-muted-foreground' : ''}>
                            {item.title}
                          </p>
                          {item.description && (
                            <p className="text-xs text-muted-foreground">{item.description}</p>
                          )}
                        </div>
                        {item.category && <Badge variant="outline">{item.category}</Badge>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="gaps">
          <Card>
            <CardHeader>
              <CardTitle>Compliance Gaps & Recommendations</CardTitle>
              <CardDescription>Areas that need attention before your SOC 2 audit</CardDescription>
            </CardHeader>
            <CardContent>
              {readiness?.recommendations?.length > 0 ? (
                <div className="space-y-4">
                  {readiness.recommendations.map((rec, idx) => (
                    <div
                      key={idx}
                      className="p-4 border rounded-lg border-l-4 border-l-yellow-500"
                      data-testid={`recommendation-${idx}`}
                    >
                      <div className="flex items-start gap-3">
                        <FileText className="h-5 w-5 text-yellow-500 mt-0.5" />
                        <div>
                          <p className="font-medium">{rec.title}</p>
                          <p className="text-sm text-muted-foreground mt-1">{rec.description}</p>
                          {rec.action && (
                            <p className="text-sm text-primary mt-2">
                              <strong>Action:</strong> {rec.action}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <p>Great job! No critical gaps identified.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
